import * as React from "react";

import { Form } from "antd";
import PickListContext from "src/context/pickListContext";
import DropdownInputAnt from "src/components/forms/DropdownInputAnt";
import DatasetAuthorizationWrapper from "src/common/DatasetAuthorizationWrapper";
import { Spinner } from "src/components/common";
import { Validation } from "src/common/utils";

import CoreAPI from "./CoreAPI";
import AssessmentResultsAPI from "src/admin/results/AssessmentResultsAPI";
import { Button } from "src/components/buttons";
import { ClientPermissions } from "src/common/Permissions";
import { Prompt } from "react-router-dom";
import { toast } from "react-toastify";

interface IProps {
	location?: any;
	match?: any;
	history?: any;
}

interface IState {
	coreTest: CoreTestResultDTO;
	isLoading: boolean;
	isDirty: boolean;
	dataLocked: boolean;
}

class CoreSkillAppraisal extends React.Component<IProps, IState> {
	public static contextType = PickListContext;

	public state = {
		coreTest: {} as CoreTestResultDTO,
		isLoading: true,
		isDirty: false,
		dataLocked: true,
	};

	public componentDidMount() {
		const { clientId, coreId, datasetId, sessionId } = this.props.match.params;
		CoreAPI.getCoreTest({
			datasetId,
			clientId,
			coreTestId: coreId,
		}).then((test) => this.setState({ coreTest: test, isLoading: false }));

		if (+sessionId === 0) {
			this.setState({ dataLocked: false });
		} else {
			AssessmentResultsAPI.getSession({ datasetId, clientId, sessionId }).then(
				(session) => {
					this.setState({
						dataLocked: session.dataLocked === false ? false : true,
					});
				}
			);
		}
	}

	public componentDidUpdate(prevProps: any, prevState: any) {
		if (
			this.state.coreTest !== prevState.coreTest &&
			this.state.coreTest.id === prevState.coreTest.id &&
			!this.state.isDirty
		) {
			this.setState({ isDirty: true });
		}
	}

	public render() {
		const { coreTest } = this.state;
		const { dataLocked } = this.state;

		const validSkillsAppraisal = {
			judgment: Validation.validateIfANumberOrNumbers(coreTest.judgementId!),
			visualSkills: Validation.validateIfANumberOrNumbers(
				coreTest.visualSkillsId!
			),
			vehicleControl: Validation.validateIfANumberOrNumbers(
				coreTest.vehicleControlId!
			),
			attentiveness: Validation.validateIfANumberOrNumbers(
				coreTest.attentivenessId!
			),
			adherence: Validation.validateIfANumberOrNumbers(
				coreTest.adherenceToRulesId!
			),
			overall: Validation.validateIfANumberOrNumbers(
				coreTest.overallPerformanceId!
			),
		};

		return this.state.isLoading !== false ? (
			<Spinner />
		) : (
			<div className="container-fluid">
				<Prompt when={this.state.isDirty} message="Exit without saving?" />
				<div className="row">
					<div className="col-md-6 col-lg-4 form-group">
						<label>Judgement/Decision making *</label>
						<Form.Item
							validateStatus={validSkillsAppraisal.judgment.validationStatus}
							help={validSkillsAppraisal.judgment.message}
							hasFeedback={true}
							required={true}
						>
							<DropdownInputAnt
								name="judgementId"
								id="judgementId"
								value={coreTest.judgementId!}
								placeholder="Pick a judgement level"
								pickListCode="Judgement"
								required={true}
								disabled={dataLocked}
								onChange={(value: any) =>
									this.onCoreTestResultChange(value, "judgementId")
								}
								sort={true}
							/>
						</Form.Item>
					</div>
					<div className="col-md-6 col-lg-4 form-group">
						<label>Visual skills *</label>
						<Form.Item
							validateStatus={
								validSkillsAppraisal.visualSkills.validationStatus
							}
							help={validSkillsAppraisal.visualSkills.message}
							hasFeedback={true}
							required={true}
						>
							<DropdownInputAnt
								name="visualSkillsId"
								id="visualSkillsId"
								pickListCode="VisualSkill"
								placeholder="Pick a visual skill"
								value={coreTest.visualSkillsId!}
								disabled={dataLocked}
								onChange={(value: any) =>
									this.onCoreTestResultChange(value, "visualSkillsId")
								}
								sort={true}
							/>
						</Form.Item>
					</div>
					<div className="col-md-6 col-lg-4 form-group">
						<label>Vehicle control *</label>
						<Form.Item
							validateStatus={
								validSkillsAppraisal.vehicleControl.validationStatus
							}
							help={validSkillsAppraisal.vehicleControl.message}
							hasFeedback={true}
							required={true}
						>
							<DropdownInputAnt
								name="vehicleControlId"
								id="vehicleControlId"
								pickListCode="VehicleControl"
								placeholder="Pick a level of vehicle control"
								value={coreTest.vehicleControlId!}
								disabled={dataLocked}
								onChange={(value: any) =>
									this.onCoreTestResultChange(value, "vehicleControlId")
								}
								sort={true}
							/>
						</Form.Item>
					</div>
					<div className="col-md-6 col-lg-4 form-group">
						<label>Attentiveness *</label>
						<Form.Item
							validateStatus={
								validSkillsAppraisal.attentiveness.validationStatus
							}
							help={validSkillsAppraisal.attentiveness.message}
							hasFeedback={true}
							required={true}
						>
							<DropdownInputAnt
								name="attentivenessId"
								id="attentivenessId"
								pickListCode="Attentiveness"
								placeholder="Pick a level of attentiveness"
								value={coreTest.attentivenessId!}
								disabled={dataLocked}
								onChange={(value: any) =>
									this.onCoreTestResultChange(value, "attentivenessId")
								}
								sort={true}
							/>
						</Form.Item>
					</div>
					<div className="col-md-6 col-lg-4 form-group">
						<label>Adherence to rules *</label>
						<Form.Item
							validateStatus={validSkillsAppraisal.adherence.validationStatus}
							help={validSkillsAppraisal.adherence.message}
							hasFeedback={true}
							required={true}
						>
							<DropdownInputAnt
								name="adherenceToRulesId"
								id="adherenceToRulesId"
								pickListCode="AdherenceToRules"
								placeholder="Pick a level of adherence to rules"
								value={coreTest.adherenceToRulesId!}
								disabled={dataLocked}
								onChange={(value: any) =>
									this.onCoreTestResultChange(value, "adherenceToRulesId")
								}
								sort={true}
							/>
						</Form.Item>
					</div>
					<div className="col-md-6 col-lg-4 form-group">
						<label>Overall performance *</label>
						<Form.Item
							validateStatus={validSkillsAppraisal.overall.validationStatus}
							help={validSkillsAppraisal.overall.message}
							hasFeedback={true}
							required={true}
						>
							<DropdownInputAnt
								name="overallPerformanceId"
								id="overallPerformanceId"
								pickListCode="OverallPerformance"
								placeholder="Pick an overall performance"
								value={coreTest.overallPerformanceId!}
								disabled={dataLocked}
								onChange={(value: any) =>
									this.onCoreTestResultChange(value, "overallPerformanceId")
								}
								sort={true}
							/>
						</Form.Item>
					</div>
				</div>
				<DatasetAuthorizationWrapper
					type="hide"
					permissions={[ClientPermissions.EditCoreTest]}
				>
					<React.Fragment>
						<Button
							onClick={this.toPreviousPage}
							className="btn-outline-primary mr-3"
						>
							Back
						</Button>
						<Button
							className="btn-primary"
							disabled={!this.isValid(validSkillsAppraisal)}
							onClick={this.toExceleratePage}
						>
							Next
						</Button>
					</React.Fragment>
				</DatasetAuthorizationWrapper>
			</div>
		);
	}

	private isValid = (validSkills: any) => {
		const validateValues: string[] = Object.keys(validSkills).map(
			(key) => validSkills[key].validationStatus
		);
		return Validation.isValid(validateValues);
	};

	private toPreviousPage = () => {
		const { organizationId, datasetId, clientId, coreId, sessionId } =
			this.props.match.params;

		const url = `/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/sessionId/${sessionId}/core/${coreId}/general-appraisal`;

		const coreTest = {
			...this.state.coreTest,
			clientId: clientId,
			datasetId: this.props.match.params.datasetId,
		};
		CoreAPI.upsertCoreTest(coreTest)
			.then(() => this.setState({ isDirty: false }))
			.then(() => this.props.history.push(url))
			.catch((e) => toast.error(e.message));
	};

	private toExceleratePage = () => {
		const usedClientId = this.props.match.params.clientId;
		const coreTest = {
			...this.state.coreTest,
			clientId: usedClientId,
			datasetId: this.props.match.params.datasetId,
		};
		CoreAPI.upsertCoreTest(coreTest)
			.then(() => this.setState({ isDirty: false }))
			.then(() => this.props.history.push("excelerate"))
			.catch((e) => toast.error(e.message));
	};

	private onCoreTestResultChange = (value: any, name: string) => {
		if (typeof value === "object") {
			const dropdownChoice = value.key;
			const coreTest = {
				...this.state.coreTest,
				[name]: dropdownChoice,
			};
			this.setState({ coreTest });
		} else {
			const coreTest = {
				...this.state.coreTest,
				[name]: value,
			};

			this.setState({ coreTest });
		}
	};
}
export default CoreSkillAppraisal;
